import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { useToast } from "@/components/ui/use-toast";

type Profile = {
  id: string;
  role: string;
  nickname?: string;
  preferred_game?: string;
};

// Cache global para evitar múltiplas chamadas
let globalCache: {
  userId: string | null;
  profile: Profile | null;
  role: string | null;
  authUser: User | null;
  loading: boolean;
  lastFetch: number;
} = {
  userId: null,
  profile: null,
  role: null,
  authUser: null,
  loading: false,
  lastFetch: 0
};

const CACHE_DURATION = 30000; // 30 segundos
const subscribers = new Set<() => void>();

// Busca a role do usuário autenticado a partir da tabela profiles (role: admin, mentor, jogador)
export function useRole() {
  // Inicializa loading como true se não há dados no cache para evitar redirecionamentos prematuros
  const [role, setRole] = useState<string | null>(globalCache.role);
  const [user, setUser] = useState<Profile | null>(globalCache.profile);
  const [loading, setLoading] = useState(globalCache.loading || (!globalCache.role && !globalCache.profile));
  const [authUser, setAuthUser] = useState<User | null>(globalCache.authUser);
  const { toast } = useToast();
  
  const isMountedRef = useRef(true);
  const subscriberRef = useRef<() => void>();

  // Função para atualizar todos os subscribers
  const notifySubscribers = () => {
    subscribers.forEach(callback => callback());
  };

  // Função para atualizar o estado local
  const updateLocalState = () => {
    if (isMountedRef.current) {
      setRole(globalCache.role);
      setUser(globalCache.profile);
      setLoading(globalCache.loading);
      setAuthUser(globalCache.authUser);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    
    // Registrar subscriber
    subscriberRef.current = updateLocalState;
    subscribers.add(subscriberRef.current);
    
    const fetchRole = async () => {
      try {
        // Definir loading como true no início para evitar redirecionamentos prematuros
        if (!globalCache.role && !globalCache.profile) {
          globalCache.loading = true;
          notifySubscribers();
        }
        
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          // Atualizar cache global
          globalCache = {
            userId: null,
            profile: null,
            role: null,
            authUser: null,
            loading: false,
            lastFetch: Date.now()
          };
          
          notifySubscribers();
          return;
        }
        
        const userId = session.user.id;
        const now = Date.now();
        
        // Verificar se já temos dados válidos no cache
        if (globalCache.userId === userId && 
            globalCache.profile && 
            globalCache.role && 
            (now - globalCache.lastFetch) < CACHE_DURATION) {
          updateLocalState();
          return;
        }
        
        // Se já estamos buscando, não fazer nova busca
        if (globalCache.loading && globalCache.userId === userId) {
          updateLocalState();
          return;
        }
        
        // Atualizar estado de loading
        globalCache.loading = true;
        globalCache.authUser = session.user;
        notifySubscribers();
        
        const { data, error } = await supabase
          .from("profiles")
          .select("id, role, nickname, preferred_game")
          .eq("id", userId)
          .maybeSingle();

        if (error) {
          console.error("[useRole] Erro ao buscar perfil:", error);
          
          // Atualizar cache com erro
          globalCache = {
            userId: null,
            profile: null,
            role: null,
            authUser: null,
            loading: false,
            lastFetch: Date.now()
          };
          
          notifySubscribers();
          
          toast({
            title: "Erro ao carregar perfil",
            description: "Não foi possível carregar suas informações. Tente fazer login novamente.",
            variant: "destructive",
          });
          return;
        }

        if (!data) {
          console.warn("[useRole] Perfil não encontrado para o usuário");
          
          // Atualizar cache sem dados
          globalCache = {
            userId: userId,
            profile: null,
            role: null,
            authUser: session.user,
            loading: false,
            lastFetch: Date.now()
          };
          
          notifySubscribers();
          return;
        }

        // Atualizar cache com dados válidos
        globalCache = {
          userId: userId,
          profile: data,
          role: data.role,
          authUser: session.user,
          loading: false,
          lastFetch: now
        };
        
        notifySubscribers();

      } catch (error) {
        console.error("[useRole] Erro inesperado:", error);
        globalCache = {
          userId: null,
          profile: null,
          role: null,
          authUser: null,
          loading: false,
          lastFetch: Date.now()
        };
        notifySubscribers();
      }
    };
    
    // Inicializar com dados do cache se disponíveis
    updateLocalState();
    
    // Buscar dados se necessário
    if (!globalCache.profile || !globalCache.role || 
        (Date.now() - globalCache.lastFetch) > CACHE_DURATION) {
      fetchRole();
    }
    
    // Listener para mudanças de auth mais otimizado
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      // Só reagir a mudanças relevantes
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        const newUserId = session?.user?.id || null;
        
        // Reset cache se mudou de usuário ou logout
        if (event === 'SIGNED_OUT' || newUserId !== globalCache.userId) {
          globalCache = {
            userId: null,
            profile: null,
            role: null,
            authUser: null,
            loading: false,
            lastFetch: 0
          };
        }
        
        // Debounce para evitar chamadas múltiplas
        setTimeout(() => {
          fetchRole();
        }, 300);
      }
    });
    
    return () => {
      isMountedRef.current = false;
      if (subscriberRef.current) {
        subscribers.delete(subscriberRef.current);
      }
      authListener?.subscription.unsubscribe();
    };
  }, []); // Sem dependências para evitar re-execução

  return { role, user, authUser, loading };
}
